import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { OptimisticPlanningService } from './optimistic-planning.service';
import { CalculationStateService } from './calculation-state.service';
import { CalculationOperation, CalculationResult } from './calculation-state.models';
import { mapPbsToPlanningBlockDetails } from './pb-details.mapper';
import { PlanningOrchestrationService, PlanningInput, PlanningResult } from '../shared/services/planning-orchestration.service';
import { SharedService } from '../shared/services/shared.service';
import { SharedActions } from '../shared/shared.actions';
import { UserFeedbackService } from '../services/user-feedback.service';
import { DevLoggerService } from '../shared/services/dev-logger.service';
import { MsalService } from '../msal';
import { PlanningBlock } from '../pb-screen/models/planning-block/planning-block.interface';
import { DepotOptimisticGuardService } from './depot-optimistic-guard.service';

/** Visual state the inline depot row indicator can be in for a given PB. */
export type DepotRowState = 'idle' | 'planning' | 'unplanning' | 'success' | 'unplanned' | 'error';

/** Resolved indicator state for a single PB row. */
export interface DepotRowStatus {
  state: DepotRowState;
  /** The operation backing this state (for dismiss / retry). Absent when idle. */
  operation?: CalculationOperation;
  /** True when the backing operation was an unplan (drives amber vs blue visuals). */
  isUnplan: boolean;
}

interface DepotUnplanInput {
  planningBlocks: PlanningBlock[];
  cancelSendToHaulier: boolean;
  source: string;
}

/**
 * DepotOptimisticPlanningService
 *
 * Bridges the depot Plan / Unplan flows into the screen-agnostic
 * {@link CalculationStateService} so the depot grids get the same non-blocking,
 * optimistic experience the planning board already has — without forking the engine.
 *
 * Responsibilities:
 *  - Start a `Calculating` operation BEFORE the background Business Central chain runs,
 *    so the affected rows lock + shimmer instantly (and mirror across tabs for free via
 *    the engine's BroadcastChannel).
 *  - Run the real chain in the background (plan via PlanningOrchestrationService, unplan
 *    via SharedService) and reconcile to Completed / Failed.
 *  - Expose per-row state for the inline indicator, plus dismiss + manual retry.
 *  - Toast only in the initiating tab (we toast from the background callback, which only
 *    runs in the tab that started the action).
 *  - Tick `expireStuckOperations()` so a hung op can never lock a row permanently (the
 *    depot screens have no activity panel to drive this).
 */
@Injectable({ providedIn: 'root' })
export class DepotOptimisticPlanningService {

  /** All operations, passthrough from the engine (already cross-tab synced). */
  public readonly operations$: Observable<CalculationOperation[]>;

  /** Set of PB ids currently calculating — drives row lock/shimmer. */
  public readonly calculatingPbIds$: Observable<Set<string>>;

  /** Retry closures keyed by the operation's local id. */
  private readonly retryActions = new Map<string, () => void>();

  private timerStarted = false;

  constructor(
    private readonly calcState: CalculationStateService,
    private readonly orchestration: PlanningOrchestrationService,
    private readonly sharedService: SharedService,
    private readonly sharedActions: SharedActions,
    private readonly userFeedback: UserFeedbackService,
    private readonly devLogger: DevLoggerService,
    private readonly authService: MsalService,
    private readonly zone: NgZone,
    private readonly guard: DepotOptimisticGuardService,
    private readonly optimisticPlanning: OptimisticPlanningService,
  ) {
    this.operations$ = this.calcState.operations$;
    this.calculatingPbIds$ = this.calcState.calculatingPbIds$;
    this.startExpiryTimer();
  }

  /** True when the user's Depot optimistic setting is on (independent of the board flag). */
  get isEnabled(): boolean {
    return this.optimisticPlanning.isDepotEnabled;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PLAN
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Optimistically plan a depot selection. Locks the affected rows instantly, then runs
   * the real prePlan → group → send chain in the background and reconciles the result.
   */
  planOptimistic(input: PlanningInput, opts?: { finalize?: boolean }): void {
    const pbs = [...input.depotInPlanningBlocks, ...input.depotOutPlanningBlocks];
    if (!pbs.length) return;
    // finalize mirrors the modal's button semantics: plain Pre-plan runs prePlan ONLY (the PB
    // must STOP at pre-planned); only Pre-plan & Send runs the full group + send chain (→ planned).
    const finalize = !!opts?.finalize;

    const account = this.authService.getAccount();
    // Client-side collisions (an in-flight operation — ours or another user's — already holds
    // these PBs or this slot) are detected and emitted SYNCHRONOUSLY by startCalculation, so the
    // flag is set before we decide whether to fire the BC chain.
    let rejectedByCollision: string | null = null;
    this.calcState.startCalculation({
      planningBlockIds: pbs.map(pb => String(pb.id)),
      userId: input.userId || account?.username || '',
      userName: account?.name || '',
      truckName: input.capacitySlot?.truck?.name,
      driverName: input.selectedDriver?.name || input.capacitySlot?.driver?.name,
      operationType: 'plan',
      planningBlockDetails: mapPbsToPlanningBlockDetails(pbs),
    }).subscribe(res => {
      if (res && res.accepted === false && res.collisions?.length) {
        rejectedByCollision = res.collisions.map(c => c.userFriendlyMessage).join(' ');
      }
    });

    // Capture the just-created operation so we can self-heal its result chip if the op
    // gets removed/overwritten before completion (see ensureTerminalChip).
    const startedOp = this.calcState.operations[0];
    const opId = startedOp ? (startedOp.localOpId || startedOp.operationId) : null;
    if (opId) this.retryActions.set(opId, () => this.planOptimistic(input, opts));

    if (rejectedByCollision) {
      // Do NOT start a second BC write chain against the same TO — that is exactly the
      // concurrent-write pattern BC rejects with its stale-record error. The failed chip
      // (with retry) is already on the row.
      this.devLogger.warn('DEPOT-OPTIMISTIC', 'Plan blocked by in-flight operation:', rejectedByCollision);
      this.userFeedback.showWarningMessageWithoutTranslating(rejectedByCollision);
      return;
    }

    // Defensive ledger: protect these rows from being silently dropped by the flaky server.
    this.guard.register(pbs, 'plan', opId || '');

    this.orchestration.executePlanning(input, { groupAfterPlan: finalize, sendAfterGroup: finalize })
      .subscribe(
        (result: PlanningResult) => {
          if (result.success) {
            this.completePlan(opId, startedOp, result, finalize);
          } else {
            this.fail(opId, result.error || 'Planning failed', 'TOASTR_MESSAGES.ERROR.PLAN_FAILED');
          }
        },
        (error) => this.fail(opId, this.errorMessage(error), 'TOASTR_MESSAGES.ERROR.PLAN_FAILED')
      );
  }

  private completePlan(opId: string | null, startedOp: CalculationOperation | undefined, result: PlanningResult, finalize: boolean): void {
    const calcResult: CalculationResult = {
      success: true,
      createdTransportOrderId: result.transportOrderId,
      // Pre-plan-only ops must read "Pre-planned" — the PB deliberately stops at that status.
      summary: { headline: finalize ? 'Planned' : 'Pre-planned', toId: result.transportOrderId }
    };
    if (opId) {
      this.calcState.completeCalculation(opId, calcResult).subscribe();
      this.ensureTerminalChip(opId, startedOp, calcResult);
    }
    // Record the resulting TO on the guard so the kept-visible badge shows what happened.
    if (startedOp?.planningBlockIds?.length) {
      this.guard.markResult(startedOp.planningBlockIds, result.transportOrderId);
    }
    this.userFeedback.showSuccessMessage('TOASTR_MESSAGES.SUCCESS.PLAN_SUCCEEDED');
  }

  /**
   * Guarantee the inline result chip survives: if the backing operation is no longer present
   * as a terminal op after completeCalculation (e.g. it was removed/overwritten mid-flight),
   * re-insert a Completed clone so the row's success/unplanned badge still shows.
   */
  private ensureTerminalChip(opId: string, startedOp: CalculationOperation | undefined, result: CalculationResult): void {
    if (!startedOp) return;
    const present = this.calcState.getOperationsSnapshot().some(o =>
      (o.operationId === opId || o.localOpId === opId) &&
      (o.status === 'Completed' || o.status === 'Failed'));
    if (present) return;
    this.calcState.upsertTerminalOperation({
      ...startedOp,
      status: 'Completed',
      completedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      result,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UNPLAN
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Optimistically unplan a depot selection. Locks the rows instantly, runs the unplan
   * call in the background and reconciles the result.
   */
  unplanOptimistic(unplanInput: DepotUnplanInput): void {
    const { planningBlocks, cancelSendToHaulier } = unplanInput;
    if (!planningBlocks.length) return;

    const account = this.authService.getAccount();
    const userId = account?.username || '';
    const pbIds = planningBlocks.map(pb => pb.id);

    // Client-side collisions are detected and emitted SYNCHRONOUSLY by startCalculation —
    // see planOptimistic for the rationale.
    let rejectedByCollision: string | null = null;
    this.calcState.startCalculation({
      planningBlockIds: pbIds.map(id => String(id)),
      userId,
      userName: account?.name || '',
      operationType: 'unplanPb',
      planningBlockDetails: mapPbsToPlanningBlockDetails(planningBlocks),
    }).subscribe(res => {
      if (res && res.accepted === false && res.collisions?.length) {
        rejectedByCollision = res.collisions.map(c => c.userFriendlyMessage).join(' ');
      }
    });

    const startedOp = this.calcState.operations[0];
    const opId = startedOp ? (startedOp.localOpId || startedOp.operationId) : null;
    if (opId) this.retryActions.set(opId, () => this.unplanOptimistic(unplanInput));

    if (rejectedByCollision) {
      // Do NOT fire a second BC write chain at the same TO (BC rejects concurrent writes with
      // its stale-record error). The failed chip (with retry) is already on the row.
      this.devLogger.warn('DEPOT-OPTIMISTIC', 'Unplan blocked by in-flight operation:', rejectedByCollision);
      this.userFeedback.showWarningMessageWithoutTranslating(rejectedByCollision);
      return;
    }

    // Defensive ledger: protect these rows from being silently dropped by the flaky server.
    this.guard.register(planningBlocks, 'unplan', opId || '');

    this.sharedService.unplan(pbIds, userId, cancelSendToHaulier).subscribe(
      (unplannedPbs: PlanningBlock[]) => {
        this.sharedActions.updatePlanningBlocksDisplayCpl(unplannedPbs);
        if (opId) {
          const calcResult: CalculationResult = { success: true, summary: { headline: 'Unplanned' } };
          this.calcState.completeCalculation(opId, calcResult).subscribe();
          this.ensureTerminalChip(opId, startedOp, calcResult);
        }
        this.userFeedback.showSuccessMessage('TOASTR_MESSAGES.SUCCESS.UNPLAN_SUCCEEDED');
      },
      (error) => this.fail(opId, this.errorMessage(error), 'TOASTR_MESSAGES.ERROR.UNPLAN_FAILED')
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ROW STATE / DISMISS / RETRY
  // ═══════════════════════════════════════════════════════════════════════════

  /** Resolve the inline indicator state for a single PB id from the current operations. */
  resolveRowStatus(pbId: string | number, ops: CalculationOperation[]): DepotRowStatus {
    const id = String(pbId);
    // Operations are prepended newest-first, so the first match is the most recent.
    const op = ops.find(o => (o.planningBlockIds || []).some(p => String(p) === id));
    if (!op) return { state: 'idle', isUnplan: false };

    const isUnplan = op.operationType === 'unplanPb';
    if (op.status === 'Calculating') {
      return { state: isUnplan ? 'unplanning' : 'planning', operation: op, isUnplan };
    }
    const failed = op.status === 'Failed' || op.result?.success === false;
    if (failed) return { state: 'error', operation: op, isUnplan };
    return { state: isUnplan ? 'unplanned' : 'success', operation: op, isUnplan };
  }

  /** Kendo grid `rowClass` helper — maps a PB id to its row css class. */
  rowCssClass(pbId: string | number): string {
    const status = this.resolveRowStatus(pbId, this.calcState.getOperationsSnapshot());
    switch (status.state) {
      case 'planning': return 'calculating';
      case 'unplanning': return 'unplanning';
      case 'success': return 'success';
      case 'unplanned': return 'unplanned';
      case 'error': return status.isUnplan ? 'unplan-error' : 'error';
      default: return '';
    }
  }

  /** Dismiss a finished (Completed/Failed) result chip. */
  dismiss(operation: CalculationOperation): void {
    if (!operation) return;
    const key = operation.localOpId || operation.operationId;
    this.retryActions.delete(key);
    this.calcState.dismissOperationLocal(operation.operationId);
    // Also release the guard pin so a filter-hidden row stops being kept visible.
    this.guard.resolve((operation.planningBlockIds || []).map(id => String(id)));
  }

  /** Manually retry a failed operation with the original inputs. */
  retry(operation: CalculationOperation): void {
    if (!operation) return;
    const key = operation.localOpId || operation.operationId;
    const action = this.retryActions.get(key);
    this.dismiss(operation);
    if (action) action();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INTERNALS
  // ═══════════════════════════════════════════════════════════════════════════

  private fail(opId: string | null, message: string, toastKey: string): void {
    if (opId) this.calcState.failCalculation(opId, message).subscribe();
    this.userFeedback.showErrorMessage(toastKey);
    this.devLogger.warn('DEPOT-OPTIMISTIC', 'Operation failed:', message);
  }

  private errorMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.feedback?.[0]?.description) return error.feedback[0].description;
    if (error?.error?.message) return error.error.message;
    if (error?.message) return error.message;
    return 'An unexpected error occurred';
  }

  /** Tick the engine's stuck-operation safety net (no activity panel on depot screens). */
  private startExpiryTimer(): void {
    if (this.timerStarted) return;
    this.timerStarted = true;
    this.zone.runOutsideAngular(() => {
      setInterval(() => {
        this.zone.run(() => this.calcState.expireStuckOperations());
      }, 1000);
    });
  }
}
